<script>
import { computed, defineComponent, ref, watchEffect } from 'vue'
import { useRAF, useElasticNumber } from '@monogrid/vue3-boost/functions'
import { templateRef, useElementSize, usePointer, useScroll } from '@vueuse/core'

export default defineComponent({
  name: 'MomentumScrolling',
  props: {
    speed: {
      type: Number,
      required: false,
      default: 3
    },
    tag: {
      type: String,
      required: false,
      default: 'div'
    },
    contentTag: {
      type: String,
      required: false,
      default: 'div'
    },
    contentClasses: {
      type: Object,
      required: false,
      default: null
    },
    contentAttributes: {
      type: Object,
      required: false,
      default: null
    },
    contentProps: {
      type: Object,
      required: false,
      default: null
    }
  },

  emits: ['scroll'],

  setup (props, { attrs, slots, emit }) {
    const { pointerType } = usePointer()
    const momentumContent = templateRef('momentumContent')
    const momentumContainer = templateRef('momentumContainer')
    const scroll = ref(0)
    const { time } = useRAF(null, true)
    const elasticScroll = useElasticNumber(0, props.speed, time)
    const { height: contentHeight } = useElementSize(momentumContent)
    const { width: containerWidth } = useElementSize(momentumContainer)
    const { y: scrollY } = useScroll(window)

    watchEffect(() => {
      elasticScroll.target = scrollY.value
    })

    watchEffect(() => {
      scroll.value = Math.round(elasticScroll.value * 100) / 100
      emit('scroll', scroll)
    })

    const momentumContentStyle = computed(() => {
      if (pointerType.value === 'mouse') {
        return {
          transform: `translate3d(0, ${-scroll.value}px, 0)`,
          width: `${containerWidth.value}px`
        }
      }
      return null
    })

    const momentumContainerStyle = computed(() => {
      if (pointerType.value === 'mouse') {
        return { height: `${contentHeight.value}px` }
      }
      return null
    })

    return {
      pointerType,
      scroll,
      momentumContainerStyle,
      momentumContentStyle
    }
  }
})
</script>

<template>
  <component
    :is="tag"
    ref="momentumContainer"
    class="momentum-container"
    :style="momentumContainerStyle"
    :class="{'momentum-container--with-mouse': pointerType === 'mouse'}"
  >
    <component
      :is="contentTag"
      ref="momentumContent"
      class="momentum-content"
      :style="momentumContentStyle"
      :class="contentClasses"
    >
      <slot :scroll="scroll"></slot>
    </component>
  </component>
</template>

<style lang="scss" scoped>
.momentum-content {
  position: relative;
  display: flex;
  width: 100%;
  max-width: 100%;
  flex-direction: column;
  backface-visibility: hidden;
  will-change: transform;
}

.momentum-container {
  position: relative;
  min-width: 100%;

  &--with-mouse {
    .momentum-content {
      position: fixed;
    }
  }
}
</style>
